import  {useState} from "react";
import {Alert, Button, TextField} from "@mui/material";
import {useQuery} from "@tanstack/react-query";
import ReactJson from "react-json-view";
import {RunDebugDetails, usePlaygroundDispatchApi} from "../../../../api/playground/dispatch";


export default function RunDetailsTab() {
    const api = usePlaygroundDispatchApi();

    const [inputRunId, setInputRunId] = useState<string>("");
    const [runId, setRunId] = useState<string>("");

    const {data, isFetching, isError, error, refetch} = useQuery(["playground", "run", runId, "debug-details"], async () => {
        if (!runId) return null as unknown as RunDebugDetails;
        return await api.getRunDebugDetails(runId);
    }, {
        enabled: !!runId,
        refetchInterval: (data) => {
            if (!data) return false;
            // If run not finished, refresh periodically
            const done = data.status === "COMPLETED" || data.status === "FAILED" || data.status === "ENDED" || data.status === "CANCELLED" || data.status === "TIMED_OUT";
            return done ? false : 5000;
        }
    });

    function submit() {
        setRunId(inputRunId.trim());
        if (runId) refetch();
    }

    return <div className="pg-run-details-tab" style={{padding: 8}}>
        <div style={{display: "flex", gap: 8, alignItems: "center", marginBottom: 12}}>
            <TextField size="small" label="Run Id" value={inputRunId}
                       onChange={(e) => setInputRunId(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && submit()}
            />
            <Button variant="contained" onClick={submit} disabled={!inputRunId || isFetching}>Load</Button>
        </div>

        {!runId && <Alert severity="info">Enter a run id and click Load</Alert>}

        {isError && <Alert severity="error">{(error as Error)?.message || "Failed to load"}</Alert>}

        {runId && isFetching && <Alert severity="info">Loading...</Alert>}

        {data && (
            <div style={{display: "grid", gap: 12}}>
                <div>
                    <h4>Summary</h4>
                    <div>Run: <code>{data.runId}</code></div>
                    <div>Status: <strong>{data.status}</strong></div>
                    <div>Started: {new Date(data.timeline.startedOn).toLocaleString()}</div>
                    {data.timeline.lastOutputOn && <div>Last Output: {new Date(data.timeline.lastOutputOn).toLocaleString()}</div>}
                    {data.timeline.finishedOn && <div>Finished: {new Date(data.timeline.finishedOn).toLocaleString()}</div>}
                    <div>Age: {(data.timeline.ageMs/1000).toFixed(1)}s</div>
                    {data.timeline.sinceLastOutMs !== undefined && <div>Since Last Output: {(data.timeline.sinceLastOutMs/1000).toFixed(1)}s</div>}
                    {data.warnings.length > 0 && <Alert severity="warning">{data.warnings.join("; ")}</Alert>}
                </div>

                <div>
                    <h4>Counts</h4>
                    <div>Canvases: {data.counts.canvases} | Phases: {data.counts.phases} | Nodes: {data.counts.nodes} | Steps: {data.counts.steps} | Attempts: {data.counts.attempts}</div>
                </div>

                <div>
                    <h4>Go Runtime</h4>
                    <div>Version: {data.runtime.goVersion}, GOMAXPROCS: {data.runtime.gomaxprocs}, Goroutines: {data.runtime.numGoroutine}</div>
                    <div>HeapAlloc: {(data.runtime.heapAllocBytes/1024/1024).toFixed(1)} MiB, HeapInuse: {(data.runtime.heapInuseBytes/1024/1024).toFixed(1)} MiB</div>
                    <div>Sys: {(data.runtime.sysBytes/1024/1024).toFixed(1)} MiB, GC Pauses: {(data.runtime.pauseTotalNs/1e9).toFixed(2)}s, NumGC: {data.runtime.numGC}</div>
                </div>

                <div>
                    <h4>Raw JSON</h4>
                    <ReactJson src={data as any} displayDataTypes={false} name={null} theme="rjv-default" indentWidth={2} style={{fontSize: "0.75rem"}}/>
                </div>
            </div>
        )}
    </div>;
}
